import _ from 'lodash';
import tinycolor from 'tinycolor2';
import { ITEM_ICONS, NUMBER_CONVERSIONS, STRING_UOM } from '@/main/app.constants';
import { ALPHA_UNCERTAIN, DASH_STYLES, EMPTY_STRING_VALUE, SAMPLE_OPTIONS } from '@/trendData/trendData.constants';
import { parseEnumerationString } from '@/utilities/enumerationString.utilities';
import type { SeriesRuntimeSample } from '@/trendData/seriesRuntime.types';
import type { DataSample, StringEnum } from '@/trendData/trendData.types';

/** The honest sample shape lives with the runtime store types; this module works on the same shape */
type SeriesDataSample = SeriesRuntimeSample;

/** Samples arrive as objects or, from some callers, as [x, y] pairs */
type RawBarSample = SeriesDataSample | [number, number | null | undefined];

/** An [x, y] or [x, lower, upper] tuple */
type SeriesDataTuple =
  | [number, number | null | undefined]
  | [number, number | null | undefined, number | null | undefined];

/** A chart-ready point: an [x, y] or [x, lower, upper] tuple, or a marker-decorated point */
type DisplayDataPoint =
  | SeriesDataTuple
  | {
      x: number;
      y: number | null | undefined;
      marker: { enabled: boolean; symbol: string; radius: number };
    };

/**
 * Chart geometry and visible range the sample transforms depend on. Passed in rather than read from
 * the duration and trend stores, so this module stays importable from a runtime store.
 */
export interface SeriesDataContext {
  /** Width of the chart in pixels */
  chartWidth: number;
  /** Visible range in milliseconds */
  displayRange: { start: number; end: number; duration: number };
}

/**
 * De-dupes data based on x-value. Data is an Array of Arrays where the first entry corresponds to the x, the
 * second value to the y-axis ([[x,y], [x,y],[x, y]].
 * Step series contain 2 values for the same x-value - this is great if you're rendering them as lines, not so when
 * you want to visualize them as bars.
 *
 * @param data - data to be de-duped.
 *
 * @return the data without duplicated values
 */
function deDupeDataByXValue(data: SeriesDataSample[]): SeriesDataSample[] {
  let current;
  let i = 1;
  const deDupedData = [];
  let previous = data[0];

  for (i; i < data.length; i++) {
    current = data[i];
    // for uncertain data the backend will sometimes return the same timestamp twice, without a value however.
    // This can lead to samples "disappear" - so we need to check for a valid value before assigning it.
    if (previous.key !== current.key || (!_.isFinite(current.value) && _.isFinite(previous.value))) {
      deDupedData.push(previous);
    }

    previous = current;
  }

  // check the last one and see if it needs to be added:
  if (previous.key !== _.last(deDupedData)!.key) {
    deDupedData.push(_.last(data)!);
  }

  return deDupedData;
}

/**
 * Prepares data to display properly as bars.
 * Currently the Backend adds a sample point at the very beginning and at the end of the display range - that's
 * great when you're rendering a series as a line, it looks odd when you try to visualize a series as bars. To
 * prevent this oddness this function removes those artificial data points. In addition, null values, that produce
 * "holes" in series mess up the bar chart display as well - so those get pruned.
 *
 * Also manages step series data to ensure proper bar display of step series.
 *
 * @param data -  an array of samples [{key: x, value: y}, {key: x, value: y, isUncertain:true}, ....]
 *
 * @returns input data, de-duped by x-values. If the first and last datapoint are exact matches for the
 * display range start and end then those data points will be removed (as they are "artificial" points inserted by
 * the backend and don't display well as bars)
 */
export function prepareDataForBarChartDisplay(
  rawData: RawBarSample[],
  displayRange: SeriesDataContext['displayRange'],
): SeriesDataSample[] {
  const startInMilliseconds = displayRange.start;
  const endInMilliseconds = displayRange.end;
  const start = startInMilliseconds * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND;
  const end = endInMilliseconds * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND;

  let data = _.chain(rawData)
    .filter((sample) =>
      Array.isArray(sample) ? _.isFinite(sample[1]) : sample.isUncertain || _.isFinite(sample.value),
    )
    .map(
      (point): SeriesDataSample =>
        Array.isArray(point) ? { key: point[0], value: point[1] as DataSample['value'] } : point,
    )
    .value();

  if (data.length <= 3) {
    return data;
  }

  if (_.first(data)!.key === start) {
    data = data.slice(1);
  }

  if (_.last(data)!.key === end) {
    data = data.slice(0, -1);
  }

  const xValues = _.map(data, 'key');

  let barChartData;
  if (xValues.length === _.uniq(xValues).length) {
    barChartData = data;
  } else {
    barChartData = deDupeDataByXValue(data);
  }

  return barChartData;
}

/**
 * This helper gets resolves string values and booleans for use on the chart
 */
export function getYValue(
  value: string | number | boolean | null | undefined,
  stringEnum?: StringEnum[] | null,
): number | null | undefined {
  if (stringEnum && !_.isNil(value) && (_.isString(value) || _.isNumber(value))) {
    const { isEnumString, enumName } = parseEnumerationString(value.toString());
    if (isEnumString) {
      return parseInt(_.result(_.find(stringEnum, ['stringValue', enumName]), 'key'), 10);
    }

    let stringValue = value.toString();
    if (stringValue === '') {
      stringValue = EMPTY_STRING_VALUE;
    }
    return _.result(_.find(stringEnum, ['stringValue', stringValue]), 'key');
  }

  if (_.isBoolean(value)) {
    return value ? 1 : 0;
  }

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  return null;
}

/**
 * We determine which points need to be shown as markers as we are moving through samples - as we are
 * constructing the data array. This helper, when called after every point is added will return all the indexes
 * that should be shown as markers
 */
export function getIndexToShowAsMarker(datums: DisplayDataPoint[], last: boolean) {
  const datumCount = datums.length;
  const valid = (idx: number) => {
    const datum = datums[idx];
    return Array.isArray(datum) ? datum[1] !== null : true;
  };
  if (datumCount === 1 && last && valid(0)) {
    return 0; // the only datum is a valid point
  } else if (datumCount === 2 && valid(0) && !valid(1)) {
    return 0; // the first datum is a valid point and the second is not
  } else if (datumCount >= 2 && last && !valid(datumCount - 2) && valid(datumCount - 1)) {
    return datumCount - 1; // the last datum is a valid point and the second to last is not
  } else if (datumCount >= 3 && !valid(datumCount - 3) && valid(datumCount - 2) && !valid(datumCount - 1)) {
    return datumCount - 2; // valid datum in between two invalid datums
  } else {
    return null;
  }
}

/**
 * Transform input samples for display. Also used to enable 'singleton' points (points that don't have neighbors).
 *
 * @returns {Object} An object with two lists of data: the unmodified samples and the display-ready data, as well
 * as the Highchart zones to indicate uncertainty.
 */
export function transformDataPoints({
  rawSamples,
  color,
  lineWidth = 1,
  minTime,
  sampleDisplayOption,
  hasBounds,
  stringEnum,
  chartWidth,
  displayRange,
}: {
  /** the input samples - can be DataSample objects or [x, y] arrays - internally transformed to mixed formats */
  rawSamples: SeriesDataSample[];
  /** color of the signal represented as a hex code (e.g. #ffffff) */
  color: string | undefined;
  /** thickness of the signal line used to scale discrete singleton markers */
  lineWidth?: number;
  /** minimum time (in milliseconds) to subtract from all samples, for use in capsule time */
  minTime: number | undefined;
  /** one of SAMPLE_OPTIONS (line, bar, ..) */
  sampleDisplayOption: string;
  /** true if data has a lower and upper bound instead of just a value. if a series `hasBounds` it cannot display uncertainty, cannot be a string series, and will not display discrete samples */
  hasBounds: boolean | undefined;
  /** an enum to map between strings and numeric values. If provided, the series is  assumed to be a string series where the values are strings (potentially in an enum format). */
  stringEnum?: StringEnum[] | null;
  /** Width of the chart in pixels */
  chartWidth: number;
  /** Visible range in milliseconds */
  displayRange: SeriesDataContext['displayRange'];
}): {
  data: DisplayDataPoint[];
  samples: SeriesDataSample[];
  zones: Array<{ value?: number | null; color?: string; dashStyle?: string }>;
} {
  // Because of the way Highcharts implements zones (https://github.com/highcharts/highcharts/issues/6928) we need
  // to calculate how much time we need to add to the last certain data point to represent a 1 pixel offset.
  const adjustment = (displayRange.duration || 1) / chartWidth;

  // Useful to determine where zones should be drawn to show uncertainty.
  const isLine = sampleDisplayOption === SAMPLE_OPTIONS.LINE || sampleDisplayOption === SAMPLE_OPTIONS.LINE_AND_SAMPLE;

  // We only compress data if the signal is visualized as a LINE; if the signal is visualized other ways we need the
  // indicators of where an actual samples are available.
  const useCompressed = sampleDisplayOption === SAMPLE_OPTIONS.LINE;

  const samples =
    !hasBounds && sampleDisplayOption === SAMPLE_OPTIONS.BAR
      ? prepareDataForBarChartDisplay(rawSamples, displayRange)
      : rawSamples;

  let compressionInProgress = false;
  const data: (number | null | undefined)[][] = [];
  const displayData: DisplayDataPoint[] = [];
  let previousValue: (number | null | undefined)[],
    previousUncertain: boolean | undefined,
    maximumCertain: number | null | undefined = null;
  _.forEach(samples, (point: SeriesDataSample) => {
    // Because we maintain nanosecond precision on the backend, not all conversions result in whole numbers. When
    // this happens, signal segments might not appear as expected, especially in Chain View (CRAB-32768). Therefore,
    // we need to round the conversion to the chart displays the segments correctly. Note: Math.ceil works as well,
    // but the ideal solution, Math.floor does not work. Ideally, we would like to be able to pass an argument into
    // the formula request that specifies timestamp precision, so we don't have to manipulate here.
    const time = _.round(point.key / NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND) - (minTime || 0);
    let returnValue: SeriesDataTuple;
    if (!hasBounds) {
      returnValue = [time, getYValue(point.value, stringEnum)];
    } else {
      const lower = getYValue(point.lower, stringEnum);
      const upper = getYValue(point.upper, stringEnum);
      if (!_.isNil(lower) && !_.isNil(upper) && lower <= upper) {
        returnValue = [time, lower, upper];
      } else {
        returnValue = [time, null, null];
      }
    }

    if (previousValue && useCompressed) {
      // Use state in the accumulator to figure out when to compress. The form of compression is: if 3 or more
      // consecutive samples have the same value, remove all but the first and last.
      if (
        previousValue[1] === returnValue[1] &&
        previousValue[2] === (returnValue as (number | null | undefined)[])[2]
      ) {
        if (compressionInProgress) {
          displayData.pop();
        } else {
          compressionInProgress = true;
        }
      } else {
        compressionInProgress = false;
      }
    }

    if (previousValue && previousValue[0] && _.isNil(maximumCertain) && point.isUncertain) {
      // Check if we should start drawing uncertainty here.
      if (isLine && _.isNil(returnValue[1]) === _.isNil(previousValue[1])) {
        // A line segment before an uncertain sample should look uncertain.
        // If one xor the other end of the segment is null, there's no line segment and we don't need to worry about
        // it. If both ends have values, we need to move the uncertainty back to the first one. We also do this if
        // both ends are null (invalid) because otherwise a discrete signal won't ever show uncertain samples.
        maximumCertain = previousValue[0] + adjustment;
      } else if (!isLine && !previousUncertain) {
        // For a bar chart or individual samples, we just want to start at the last certain sample, valid or not.
        maximumCertain = previousValue[0] + adjustment;
      }
    }

    data.push(returnValue);
    displayData.push(returnValue);

    if (!hasBounds && sampleDisplayOption !== SAMPLE_OPTIONS.BAR) {
      // Display isolated samples as discrete points on the trend. To know if a point is a 'singleton' we look at
      // it's surrounding points - if they are both null or, if the  point is a beginning or endpoint and the only
      // possible neighbor is null we know we need to add a marker for it to show.
      const markerIndex = getIndexToShowAsMarker(displayData, point === _.last(samples));
      if (markerIndex !== null) {
        const [x, y] = displayData[markerIndex] as [number, number | null | undefined];
        displayData[markerIndex] = {
          x,
          y,
          marker: {
            enabled: true,
            symbol: 'square',
            radius: lineWidth + 0.5,
          },
        };
      }
    }

    previousValue = returnValue;
    previousUncertain = point.isUncertain;
  });

  // Check for special case with no certain, valid samples
  const firstValidSample = _.find(samples, (sample) => !_.isNil(getYValue(sample.value, stringEnum)));
  if (_.get(firstValidSample, 'isUncertain')) {
    // Turn everything uncertain, even though we never saw a certain -> uncertain transition.
    maximumCertain = data[0][0];
  }

  return {
    data: displayData,
    samples: rawSamples,
    zones: _.isNil(maximumCertain)
      ? []
      : [
          { value: maximumCertain },
          {
            color: tinycolor(color).setAlpha(ALPHA_UNCERTAIN).toString(),
            dashStyle: DASH_STYLES.DASH,
          },
        ],
  };
}

export function createStringEnum(
  samples: Array<{ key: number; value?: number | string | boolean | null }> | undefined,
): StringEnum[] {
  let stringValueMap: Record<string, number> | null = null;
  const returnStringEnum: StringEnum[] = [];

  _.forEach(samples, (point) => {
    if (_.isString(point.value)) {
      let stringPointValue = point.value.toString();
      if (stringPointValue === '') {
        stringPointValue = EMPTY_STRING_VALUE;
      }
      const { isEnumString, enumValue, enumName } = parseEnumerationString(stringPointValue);
      if (isEnumString) {
        // If the regex matches, then the sample is an enumerated value
        if (_.isUndefined(_.find(returnStringEnum, ['stringValue', enumName]))) {
          returnStringEnum.push({
            key: enumValue,
            stringValue: enumName,
          });
        }
      } else {
        // Create map object containing sorted string names that have increasing integer values starting at zero
        if (!stringValueMap) {
          stringValueMap = _.chain(samples)
            .map('value')
            .uniq()
            .sort()
            .transform(
              function (map, val, i) {
                // Indexing coerces keys to strings at runtime; say so, since values can be booleans
                if (val !== undefined && val !== null) map[String(val)] = i;
              },
              {} as Record<string, number>,
            )
            .mapKeys((_val, key) => (key === '' ? EMPTY_STRING_VALUE : key))
            .value();
        }

        // String
        if (_.isUndefined(_.find(returnStringEnum, ['stringValue', stringPointValue]))) {
          returnStringEnum.push({
            key: stringValueMap[stringPointValue],
            stringValue: stringPointValue,
          });
        }
      }
    }
  });

  return returnStringEnum;
}

export function getDataProps({
  payload,
  sampleDisplayOption,
  minTime,
  hasBounds,
  context,
}: {
  /** Object container for arguments */
  payload: {
    stringEnum?: StringEnum[];
    /** Array of data points */
    samples: SeriesRuntimeSample[];
    /** Unit of Measure to be displayed */
    valueUnitOfMeasure: string;
    timingInformation: string | undefined;
    meterInformation: string | undefined;
    interpolationMethod: string | undefined;
    capsuleSegmentSamples: SeriesDataSample[];
    color: string | undefined;
    lineWidth?: number;
  };
  /** One of SAMPLE_OPTIONS (line, bar, ..) */
  sampleDisplayOption: string;
  /** startTime if series from capsule */
  minTime?: number;
  /** true if data has a lower and upper bound instead of just a value */
  hasBounds?: boolean;
  /** Chart geometry and visible range the transforms depend on */
  context: SeriesDataContext;
}) {
  const isStringSeries = payload.valueUnitOfMeasure === STRING_UOM;

  const props: {
    valueUnitOfMeasure: string;
    timingInformation: string | undefined;
    meterInformation: string | undefined;
    interpolationMethod: string | undefined;
    data: DisplayDataPoint[];
    samples?: SeriesDataSample[];
    zones?: Array<{ value?: number | null; color?: string; dashStyle?: string }>;
    isStringSeries?: boolean;
    iconClass?: string;
    stringEnum?: StringEnum[];
    calculatedStringEnum?: StringEnum[];
    capsuleSegmentSamples?: SeriesDataSample[];
    capsuleSegmentData?: DisplayDataPoint[];
  } = {
    valueUnitOfMeasure: payload.valueUnitOfMeasure,
    timingInformation: payload.timingInformation,
    meterInformation: payload.meterInformation,
    interpolationMethod: payload.interpolationMethod,
    data: [],
  };

  if (_.get(payload, 'samples.length')) {
    if (!hasBounds && isStringSeries) {
      const stringEnum = payload.stringEnum ?? createStringEnum(payload.samples);
      const capsuleSegment = exposedForTesting.transformDataPoints({
        rawSamples: payload.capsuleSegmentSamples,
        color: payload.color,
        lineWidth: payload.lineWidth,
        minTime,
        sampleDisplayOption: '',
        hasBounds: false,
        stringEnum,
        ...context,
      });

      _.assign(
        props,
        exposedForTesting.transformDataPoints({
          rawSamples: payload.samples,
          color: payload.color,
          lineWidth: payload.lineWidth,
          minTime,
          sampleDisplayOption,
          hasBounds: false,
          stringEnum,
          ...context,
        }),
        {
          isStringSeries,
          iconClass: ITEM_ICONS.STRING_SERIES,
          stringEnum,
          calculatedStringEnum: stringEnum,
          capsuleSegmentSamples: capsuleSegment.samples,
          capsuleSegmentData: capsuleSegment.data,
        },
      );
    } else {
      const capsuleSegment = exposedForTesting.transformDataPoints({
        rawSamples: payload.capsuleSegmentSamples,
        color: payload.color,
        lineWidth: payload.lineWidth,
        minTime,
        sampleDisplayOption: '',
        hasBounds,
        stringEnum: null,
        ...context,
      });
      _.assign(
        props,
        exposedForTesting.transformDataPoints({
          rawSamples: payload.samples,
          color: payload.color,
          lineWidth: payload.lineWidth,
          minTime,
          sampleDisplayOption,
          hasBounds,
          stringEnum: null,
          ...context,
        }),
        {
          // Explicitly reset string-series state because item updates are merged into existing series objects.
          isStringSeries: false,
          iconClass: ITEM_ICONS.SERIES,
          stringEnum: undefined,
          calculatedStringEnum: undefined,
          capsuleSegmentSamples: capsuleSegment.samples,
          capsuleSegmentData: capsuleSegment.data,
        },
      );
    }
  }

  return props;
}

/** Whether a customization change invalidates the derived render data, which reads both fields */
export function affectsSeriesRenderData(customizations: Record<string, unknown>): boolean {
  return Boolean(customizations.sampleDisplayOption) || 'lineWidth' in customizations;
}

/**
 * Extracts the samples that fall inside a capsule's time range. The bounding samples are included so
 * the drawn segment reaches the capsule edges.
 */
export function extractSamplesForCapsule(
  allSamples: SeriesDataSample[],
  startTime: number,
  endTime: number,
): SeriesDataSample[] {
  const startIndexCandidate = _.sortedIndexBy<SeriesDataSample>(
    allSamples,
    { key: startTime * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND },
    'key',
  );
  const startIndex =
    startIndexCandidate < allSamples.length && 'value' in (allSamples[startIndexCandidate] ?? {})
      ? startIndexCandidate
      : startIndexCandidate + 1;

  const endIndex = _.sortedIndexBy<SeriesDataSample>(
    allSamples,
    { key: endTime * NUMBER_CONVERSIONS.NANOSECONDS_PER_MILLISECOND },
    'key',
  );

  return allSamples.slice(startIndex, endIndex + 1);
}

export const exposedForTesting = {
  transformDataPoints,
};
